import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { enqueueOfflineRequest } from '../utils/offlineQueue';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS, hasEffectivePermission } from '../utils/accessControl';
import '../styles/Tasks.css';

const API_BASE = API_BASE_URL;

const INITIAL_FORM_STATE = {
  project_id: '',
  board_node_id: '',
  title: '',
  assigned_to: '',
  due_date: '',
  priority: 'medium',
  status: 'to_do',
  description: '',
};

const ALLOWED_STATUS_FILTERS = new Set(['all', 'to_do', 'in_progress', 'completed']);

const flattenBoardOptions = (nodes, level = 0, collector = []) => {
  nodes.forEach((node) => {
    collector.push({
      id: Number(node.id),
      name: `${'  '.repeat(level)}${node.name}`,
      level,
    });
    flattenBoardOptions(node.children || [], level + 1, collector);
  });
  return collector;
};

const findNodeById = (nodes, nodeId) => {
  for (const node of nodes) {
    if (Number(node.id) === Number(nodeId)) return node;
    const found = findNodeById(node.children || [], nodeId);
    if (found) return found;
  }
  return null;
};

const collectDescendantIds = (node) => {
  const ids = [Number(node.id)];
  (node.children || []).forEach((child) => {
    ids.push(...collectDescendantIds(child));
  });
  return ids;
};

const buildLocalTaskRecord = (formState, users, boardNameMap, fallbackId, existingTask = null) => {
  const assignedUser = users.find((user) => Number(user.id) === Number(formState.assigned_to));
  return {
    ...(existingTask || {}),
    id: existingTask?.id || fallbackId,
    project_id: Number(formState.project_id || existingTask?.project_id || 0),
    board_node_id: formState.board_node_id ? Number(formState.board_node_id) : null,
    title: String(formState.title || existingTask?.title || '').trim(),
    description: formState.description || existingTask?.description || '',
    assigned_to: formState.assigned_to ? Number(formState.assigned_to) : null,
    due_date: formState.due_date || null,
    priority: formState.priority || existingTask?.priority || 'medium',
    status: formState.status || existingTask?.status || 'to_do',
    updated_at: new Date().toISOString(),
    is_offline_pending: true,
    offline_board_name: formState.board_node_id
      ? (boardNameMap.get(Number(formState.board_node_id)) || existingTask?.offline_board_name || '')
      : '',
    offline_assigned_name: assignedUser
      ? (assignedUser.job_title ? `${assignedUser.username} (${assignedUser.job_title})` : assignedUser.username)
      : (existingTask?.offline_assigned_name || ''),
  };
};

const Tasks = () => {
  const { user, permissions } = useAuth();
  const location = useLocation();
  const { projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState(projectId || 'all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [focusedTaskId, setFocusedTaskId] = useState(null);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiCandidates, setAiCandidates] = useState([]);
  const [aiError, setAiError] = useState('');
  const [boardTree, setBoardTree] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState('');
  const canManageTasks = hasEffectivePermission(user, permissions, 'can_manage_tasks');
  const canUseAiAssignment = [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER].includes(Number(user?.role_id));
  const canManageBoards = [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(Number(user?.role_id)) && canManageTasks;

  const activeProjectForBoards = useMemo(() => {
    if (projectId) return Number(projectId);
    if (projectFilter !== 'all') return Number(projectFilter);
    if (formState.project_id) return Number(formState.project_id);
    return 0;
  }, [formState.project_id, projectFilter, projectId]);

  const fetchProjectAndTasks = useCallback(async () => {
    try {
      setLoading(true);
      const [projectResponse, tasksResponse] = await Promise.all([
        apiClient.get(`${API_BASE}/projects/${projectId}`),
        apiClient.get(`${API_BASE}/tasks/project/${projectId}`),
      ]);
      setProject(projectResponse.data || null);
      setTasks(Array.isArray(tasksResponse.data) ? tasksResponse.data : []);
      setError(null);
    } catch (fetchError) {
      setError('Görevler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchAllTasks = useCallback(async () => {
    try {
      setLoading(true);
      const tasksResponse = await apiClient.get(`${API_BASE}/tasks`);
      let allTasks = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];
      if (projectFilter !== 'all') {
        allTasks = allTasks.filter((task) => Number(task.project_id) === Number(projectFilter));
      }
      setTasks(allTasks);
      setError(null);
    } catch (fetchError) {
      setError('Görevler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [projectFilter]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (fetchError) {
      setProjects([]);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/users/options`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (fetchError) {
      try {
        const fallback = await apiClient.get(`${API_BASE}/users`);
        setUsers(Array.isArray(fallback.data) ? fallback.data : []);
      } catch (fallbackError) {
        setUsers([]);
      }
    }
  }, []);

  const fetchBoardTree = useCallback(async (projectIdValue) => {
    const numericProjectId = Number(projectIdValue || 0);
    if (!numericProjectId) {
      setBoardTree([]);
      return;
    }

    try {
      setBoardLoading(true);
      const response = await apiClient.get(`${API_BASE}/tasks/boards/project/${numericProjectId}/tree`);
      setBoardTree(Array.isArray(response.data?.tree) ? response.data.tree : []);
      setBoardError('');
    } catch (fetchError) {
      setBoardTree([]);
      setBoardError(fetchError.response?.data?.message || 'Katman listesi yüklenemedi.');
    } finally {
      setBoardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProjectAndTasks();
    } else {
      fetchAllTasks();
      fetchProjects();
    }
    fetchUsers();
  }, [projectId, fetchProjectAndTasks, fetchAllTasks, fetchProjects, fetchUsers]);

  useEffect(() => {
    if (!projectId && projectFilter !== 'all') {
      fetchAllTasks();
    }
  }, [projectFilter, projectId, fetchAllTasks]);

  useEffect(() => {
    if (activeProjectForBoards) {
      fetchBoardTree(activeProjectForBoards);
    } else {
      setBoardTree([]);
    }
  }, [activeProjectForBoards, fetchBoardTree]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = String(searchParams.get('status') || '').trim().toLowerCase();
    const focusTask = Number(searchParams.get('focusTask') || 0);
    if (!status) {
      setStatusFilter('all');
    } else {
      setStatusFilter(ALLOWED_STATUS_FILTERS.has(status) ? status : 'all');
    }

    if (focusTask > 0) {
      setFocusedTaskId(focusTask);
      setStatusFilter('all');
      setBoardFilter('all');
    } else {
      setFocusedTaskId(null);
    }
  }, [location.search]);

  const getUserDisplay = (userId) => {
    const matched = users.find((user) => Number(user.id) === Number(userId));
    if (!matched) return 'Bilinmiyor';
    return matched.job_title ? `${matched.username} (${matched.job_title})` : matched.username;
  };

  const openCreateForm = () => {
    if (!canManageTasks) return;
    setEditingTask(null);
    setFormState({
      ...INITIAL_FORM_STATE,
      project_id: projectId || '',
      board_node_id: boardFilter !== 'all' ? String(boardFilter) : '',
    });
    setAiRecommendation(null);
    setAiCandidates([]);
    setAiError('');
    setShowForm(true);
  };

  const startEditing = (task) => {
    if (!canManageTasks) return;
    setEditingTask(task);
    setFormState({
      project_id: String(task.project_id || projectId || ''),
      board_node_id: task.board_node_id ? String(task.board_node_id) : '',
      title: task.title || '',
      assigned_to: task.assigned_to ? String(task.assigned_to) : '',
      due_date: task.due_date ? String(task.due_date).split('T')[0] : '',
      priority: task.priority || 'medium',
      status: task.status || 'to_do',
      description: task.description || '',
    });
    setAiRecommendation(null);
    setAiCandidates([]);
    setAiError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormState(INITIAL_FORM_STATE);
    setAiRecommendation(null);
    setAiCandidates([]);
    setAiError('');
  };

  const handleCreateTask = async () => {
    if (!canManageTasks) return;
    const payload = {
      ...formState,
      project_id: Number(formState.project_id || projectId || 0) || '',
    };
    try {
      if (!window.navigator.onLine) {
        const localTask = buildLocalTaskRecord(
          payload,
          users,
          boardNameMap,
          `offline-task-${Date.now()}`
        );
        setTasks((prev) => [localTask, ...prev]);
        enqueueOfflineRequest({
          method: 'post',
          url: `${API_BASE}/tasks`,
          data: payload,
          label: `Görev bekliyor: ${payload.title}`,
        });
        setStatusMessage('Bağlantı yok. Yeni görev kuyruğa alındı ve internet gelince gönderilecek.');
        setError(null);
        closeForm();
        return;
      }

      await apiClient.post(`${API_BASE}/tasks`, payload);
      if (projectId) {
        await fetchProjectAndTasks();
      } else {
        await fetchAllTasks();
      }
      setStatusMessage('Görev oluşturuldu.');
      setError(null);
      closeForm();
    } catch (createError) {
      setError('Görev oluşturulurken hata oluştu.');
    }
  };

  const handleUpdateTask = async () => {
    if (!canManageTasks) return;
    if (!editingTask?.id) return;
    const payload = {
      ...formState,
      project_id: Number(formState.project_id || projectId || editingTask.project_id || 0) || '',
    };
    try {
      if (!window.navigator.onLine) {
        const localTask = buildLocalTaskRecord(
          payload,
          users,
          boardNameMap,
          editingTask.id,
          editingTask
        );
        setTasks((prev) => prev.map((task) => (
          Number(task.id) === Number(editingTask.id) ? localTask : task
        )));
        enqueueOfflineRequest({
          method: 'put',
          url: `${API_BASE}/tasks/${editingTask.id}`,
          data: payload,
          label: `Görev güncellemesi bekliyor: ${payload.title}`,
        });
        setStatusMessage('Bağlantı yok. Görev güncellemesi kuyruğa alındı.');
        setError(null);
        closeForm();
        return;
      }

      await apiClient.put(`${API_BASE}/tasks/${editingTask.id}`, payload);
      if (projectId) {
        await fetchProjectAndTasks();
      } else {
        await fetchAllTasks();
      }
      setStatusMessage('Görev güncellendi.');
      setError(null);
      closeForm();
    } catch (updateError) {
      setError('Görev güncellenirken hata oluştu.');
    }
  };

  const handleDeleteTask = async (id) => {
    if (!canManageTasks) return;
    if (!window.confirm('Bu görevi silmek istediğinizden emin misiniz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/tasks/${id}`);
      if (projectId) {
        await fetchProjectAndTasks();
      } else {
        await fetchAllTasks();
      }
    } catch (deleteError) {
      setError('Görev silinirken hata oluştu.');
    }
  };

  const requestAiAssignee = async () => {
    if (!canUseAiAssignment) return;
    try {
      setAiLoading(true);
      setAiError('');
      const response = await apiClient.post(
        `${API_BASE}/tasks/ai-assignee-suggestions`,
        {
          project_id: Number(formState.project_id || projectId || 0) || null,
        }
      );
      setAiRecommendation(response.data?.recommended || null);
      setAiCandidates(Array.isArray(response.data?.suggestions) ? response.data.suggestions : []);
      if (response.data?.recommended?.id) {
        setFormState((prev) => ({ ...prev, assigned_to: String(response.data.recommended.id) }));
      }
    } catch (aiRequestError) {
      setAiError(aiRequestError.response?.data?.message || 'AI atama önerisi alınamadı.');
      setAiRecommendation(null);
      setAiCandidates([]);
    } finally {
      setAiLoading(false);
    }
  };

  const boardOptions = useMemo(() => flattenBoardOptions(boardTree), [boardTree]);
  const selectedBoardNode = useMemo(
    () => (boardFilter === 'all' ? null : findNodeById(boardTree, Number(boardFilter))),
    [boardFilter, boardTree]
  );
  const boardNameMap = useMemo(() => {
    const map = new Map();
    boardOptions.forEach((item) => map.set(Number(item.id), item.name.trim()));
    return map;
  }, [boardOptions]);

  const createBoardNode = async (parentId = null) => {
    if (!canManageBoards) return;
    if (!activeProjectForBoards) {
      setBoardError('Katman oluşturmak için önce proje seçin.');
      return;
    }
    const name = window.prompt('Katman adı:', '');
    if (!name || !String(name).trim()) return;
    try {
      await apiClient.post(
        `${API_BASE}/tasks/boards/project/${activeProjectForBoards}/nodes`,
        {
          parent_id: parentId,
          name: String(name).trim(),
        }
      );
      await fetchBoardTree(activeProjectForBoards);
      setBoardError('');
    } catch (requestError) {
      setBoardError(requestError.response?.data?.message || 'Katman oluşturulamadı.');
    }
  };

  const updateBoardNode = async () => {
    if (!canManageBoards) return;
    if (!selectedBoardNode) return;
    const nextName = window.prompt('Yeni katman adı:', selectedBoardNode.name || '');
    if (!nextName || !String(nextName).trim()) return;
    try {
      await apiClient.put(
        `${API_BASE}/tasks/boards/nodes/${selectedBoardNode.id}`,
        {
          name: String(nextName).trim(),
          parent_id: selectedBoardNode.parent_id || null,
          sort_order: selectedBoardNode.sort_order || 0,
        }
      );
      await fetchBoardTree(activeProjectForBoards);
      setBoardError('');
    } catch (requestError) {
      setBoardError(requestError.response?.data?.message || 'Katman güncellenemedi.');
    }
  };

  const deleteBoardNode = async () => {
    if (!canManageBoards) return;
    if (!selectedBoardNode) return;
    if (!window.confirm(`"${selectedBoardNode.name}" katmanı silinsin mi?`)) return;
    try {
      await apiClient.delete(`${API_BASE}/tasks/boards/nodes/${selectedBoardNode.id}`);
      setBoardFilter('all');
      await fetchBoardTree(activeProjectForBoards);
      setBoardError('');
    } catch (requestError) {
      setBoardError(requestError.response?.data?.message || 'Katman silinemedi.');
    }
  };

  useEffect(() => {
    if (boardFilter === 'all') return;
    const selectedExists = boardOptions.some((node) => Number(node.id) === Number(boardFilter));
    if (!selectedExists) {
      setBoardFilter('all');
    }
  }, [boardOptions, boardFilter]);

  const boardScopedTasks = useMemo(() => {
    if (boardFilter === 'all') return tasks;
    if (!selectedBoardNode) return tasks;
    const descendantIds = new Set(collectDescendantIds(selectedBoardNode));
    return tasks.filter((task) => descendantIds.has(Number(task.board_node_id || 0)));
  }, [tasks, boardFilter, selectedBoardNode]);

  const visibleTasks = useMemo(
    () => (statusFilter === 'all'
      ? boardScopedTasks
      : boardScopedTasks.filter((task) => task.status === statusFilter)),
    [statusFilter, boardScopedTasks]
  );

  const getTasksByStatus = (status) => visibleTasks.filter((task) => task.status === status);

  useEffect(() => {
    if (!focusedTaskId) return;
    const target = document.querySelector(`[data-task-id="${focusedTaskId}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedTaskId, visibleTasks]);

  const getPriorityColor = (priority) => {
    switch (String(priority || '').toLowerCase()) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-medium';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (loading) return <div className="tasks-container loading">Yükleniyor...</div>;

  const statuses = ['to_do', 'in_progress', 'completed'];
  const statusLabels = {
    to_do: 'Yapılacak',
    in_progress: 'Devam Ediyor',
    completed: 'Tamamlandı',
  };

  return (
    <div className="tasks-container">
      <div className="tasks-header">
        <h1>{projectId && project ? `${project.name} - ` : ''}Görevler</h1>
        {canManageTasks && (
          <button className="btn-add-task" onClick={openCreateForm} type="button">
            Yeni Görev
          </button>
        )}
      </div>

      {error && <div className="error-alert">{error}</div>}
      {statusMessage && <div className="success-alert">{statusMessage}</div>}

      <div className="task-board-selector">
        <div className="task-board-title">Katman Panoları</div>
        {!activeProjectForBoards ? (
          <div className="task-board-empty">Katmanları yönetmek için önce tek bir proje seçin.</div>
        ) : (
          <>
            <div className="task-board-actions">
              <button
                className={`filter-btn ${boardFilter === 'all' ? 'active' : ''}`}
                onClick={() => setBoardFilter('all')}
                type="button"
              >
                Tümü
              </button>
              {boardOptions.map((node) => (
                <button
                  key={node.id}
                  className={`filter-btn ${Number(boardFilter) === Number(node.id) ? 'active' : ''}`}
                  onClick={() => setBoardFilter(String(node.id))}
                  title={node.name}
                  type="button"
                >
                  {node.name}
                </button>
              ))}
            </div>
            {canManageBoards && (
              <div className="task-board-actions task-board-manage-row">
                <button type="button" className="btn-small btn-edit" onClick={() => createBoardNode(null)}>
                  Kök Katman Ekle
                </button>
                <button
                  type="button"
                  className="btn-small btn-edit"
                  disabled={boardFilter === 'all'}
                  onClick={() => createBoardNode(boardFilter === 'all' ? null : Number(boardFilter))}
                >
                  Alt Katman Ekle
                </button>
                <button
                  type="button"
                  className="btn-small btn-edit"
                  disabled={boardFilter === 'all'}
                  onClick={updateBoardNode}
                >
                  Katman Düzenle
                </button>
                <button
                  type="button"
                  className="btn-small btn-delete"
                  disabled={boardFilter === 'all'}
                  onClick={deleteBoardNode}
                >
                  Katman Sil
                </button>
              </div>
            )}
          </>
        )}
        {boardLoading && <div className="task-board-empty">Katmanlar yükleniyor...</div>}
        {boardError && <div className="form-error">{boardError}</div>}
      </div>

      <div className="tasks-controls">
        {!projectId && projects.length > 0 && (
          <div className="project-filter">
            <button
              className={`filter-btn ${projectFilter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setProjectFilter('all');
                setBoardFilter('all');
              }}
              type="button"
            >
              Tüm Projeler
            </button>
            {projects.slice(0, 5).map((proj) => (
              <button
                key={proj.id}
                className={`filter-btn ${projectFilter === proj.id.toString() ? 'active' : ''}`}
                onClick={() => {
                  setProjectFilter(proj.id.toString());
                  setBoardFilter('all');
                }}
                title={proj.name}
                type="button"
              >
                {proj.name.substring(0, 15)}
              </button>
            ))}
          </div>
        )}
        <div className="status-filter">
          <button className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')} type="button">
            Tümü ({tasks.length})
          </button>
          {Object.keys(statusLabels).map((status) => (
            <button
              key={status}
              className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
              onClick={() => setStatusFilter(status)}
              type="button"
            >
              {statusLabels[status]} ({tasks.filter((task) => task.status === status).length})
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTask ? 'Görevi Düzenle' : 'Yeni Görev Oluştur'}</h2>
              <button className="btn-close" onClick={closeForm} type="button">X</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (editingTask) handleUpdateTask();
                else handleCreateTask();
              }}
            >
              <div className="form-group">
                <label className="form-label">Görev Adı *</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={formState.title}
                  onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Üst Görev / İş Paketi</label>
                <select
                  name="board_node_id"
                  className="form-select"
                  value={formState.board_node_id}
                  onChange={(e) => setFormState((prev) => ({ ...prev, board_node_id: e.target.value }))}
                >
                  <option value="">Yok (Bağımsız görev)</option>
                  {boardOptions
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Görevin Atandığı Kişi</label>
                <select
                  name="assigned_to"
                  className="form-select"
                  value={formState.assigned_to}
                  onChange={(e) => setFormState((prev) => ({ ...prev, assigned_to: e.target.value }))}
                >
                  <option value="">Seçiniz...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username}{user.job_title ? ` (${user.job_title})` : ''}
                    </option>
                  ))}
                </select>
                {canUseAiAssignment && (
                  <div style={{ marginTop: '0.55rem', display: 'flex', gap: '0.45rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn-small btn-edit" onClick={requestAiAssignee} disabled={aiLoading}>
                      {aiLoading ? 'AI Öneri Hazırlanıyor...' : 'AI Atama Önerisi'}
                    </button>
                    {aiRecommendation && (
                      <small>
                        Öneri: <strong>{aiRecommendation.username}</strong> ({aiRecommendation.active_task_count} aktif görev)
                      </small>
                    )}
                  </div>
                )}
                {aiError && <div className="form-error">{aiError}</div>}
                {canUseAiAssignment && aiCandidates.length > 1 && (
                  <div style={{ marginTop: '0.4rem', color: '#46698f', fontSize: '0.8rem' }}>
                    Diğer adaylar: {aiCandidates.slice(1, 4).map((item) => item.username).join(', ')}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Bitiş Tarihi</label>
                <input
                  type="date"
                  name="due_date"
                  className="form-input"
                  value={formState.due_date}
                  onChange={(e) => setFormState((prev) => ({ ...prev, due_date: e.target.value }))}
                />
                <small style={{ color: '#6a84a3' }}>
                  Atama yapıldığında kişiye bildirim gider ve görev takvimine otomatik eklenir.
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Öncelik</label>
                <select
                  name="priority"
                  className="form-select"
                  value={formState.priority}
                  onChange={(e) => setFormState((prev) => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="low">Düşük</option>
                  <option value="medium">Orta</option>
                  <option value="high">Yüksek</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Durum</label>
                <select
                  name="status"
                  className="form-select"
                  value={formState.status}
                  onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="to_do">Yapılacak</option>
                  <option value="in_progress">Devam Ediyor</option>
                  <option value="completed">Tamamlandı</option>
                </select>
              </div>

              {!projectId && (
                <div className="form-group">
                  <label className="form-label">Proje *</label>
                  <select
                    name="project_id"
                    className="form-select"
                    value={formState.project_id}
                    onChange={(e) => setFormState((prev) => ({ ...prev, project_id: e.target.value }))}
                    required
                  >
                    <option value="">Proje Seçiniz</option>
                    {projects.map((proj) => (
                      <option key={proj.id} value={proj.id}>{proj.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <textarea
                  name="description"
                  className="form-textarea"
                  value={formState.description}
                  onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingTask ? 'Güncelle' : 'Oluştur'}
                </button>
                <button type="button" className="btn-cancel" onClick={closeForm}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {visibleTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          Görev bulunamadı.
        </div>
      ) : (
        <div className="kanban-board">
          {statuses.map((status) => (
            <div key={status} className="kanban-column">
              <div className="column-header">
                <h3 className="column-title">{statusLabels[status]}</h3>
                <span className="task-count">{getTasksByStatus(status).length}</span>
              </div>
              <div className="column-content">
                {getTasksByStatus(status).length === 0 ? (
                  <div className="empty-column">
                    <div className="empty-icon">--</div>
                    <p className="empty-text">Henüz görev yok</p>
                  </div>
                ) : (
                  getTasksByStatus(status).map((task) => (
                    <div
                      key={task.id}
                      className={`task-card ${Number(focusedTaskId) === Number(task.id) ? 'focused-task-card' : ''}`}
                      data-task-id={task.id}
                    >
                      <h4 className="task-title">{task.title}</h4>
                      {task.description && (
                        <p className="task-description">{task.description}</p>
                      )}
                      {task.board_node_id && (
                        <div className="task-parent-chip">
                          Katman: {boardNameMap.get(Number(task.board_node_id)) || task.offline_board_name || task.board_node_id}
                        </div>
                      )}
                      {task.is_offline_pending && (
                        <div className="task-parent-chip">Senkron bekliyor</div>
                      )}
                      <div className="task-meta">
                        <div className="task-assigned">
                          {task.assigned_to ? (
                            <>
                              <div className="task-avatar">
                                {((users.find((u) => Number(u.id) === Number(task.assigned_to))?.username || task.offline_assigned_name || '?')
                                  .charAt(0)
                                  .toUpperCase())}
                              </div>
                              <span>{task.offline_assigned_name || getUserDisplay(task.assigned_to)}</span>
                            </>
                          ) : (
                            <span>Atanan kişi yok</span>
                          )}
                        </div>
                        {task.due_date && (
                          <div className="task-due-date">
                            Son Tarih: {formatDate(task.due_date)}
                          </div>
                        )}
                        {task.priority && (
                          <div className={`task-priority ${getPriorityColor(task.priority)}`}>
                            {task.priority === 'high' ? 'Yüksek' : task.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </div>
                        )}
                      </div>
                      {canManageTasks && (
                        <div className="task-actions">
                          <button className="btn-task-edit" onClick={() => startEditing(task)} type="button" disabled={Boolean(task.is_offline_pending)}>
                            Düzenle
                          </button>
                          <button className="btn-task-delete" onClick={() => handleDeleteTask(task.id)} type="button" disabled={Boolean(task.is_offline_pending)}>
                            Sil
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tasks;




